import { put, takeLatest } from "redux-saga/effects";

import {
  publishSolutionRequest,
  publishSolutionSuccess,
  publishSolutionFailure,

  updatePublishedSolutionRequest,
  updatePublishedSolutionSuccess,
  updatePublishedSolutionFailure,

  deletePublishedSolutionRequest,
  deletePublishedSolutionSuccess,
  deletePublishedSolutionFailure,

  fetchPublishedRequest,
  fetchPublishedSuccess,
  fetchPublishedFailure,
} from "./publishedSolutionsSlice";

import { fastApiURL } from "../../utils/config";
import { normalizeSolutionFormPayload } from "../../components/CreateSolutionField/solutionFormDefaults";
import { fetchDraftsRequest } from "../drafts/draftsSlice";
import TokenManager from "../../utils/tokenManager";

// =========================
// PUBLISH DRAFT
// =========================

function* publishSolutionSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    const { draftId, projectId } = action.payload;
    const response = yield fetch(
      `${fastApiURL}/api/drafts/${draftId}/publish`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = yield response.json();

      throw new Error(
        errorData.detail || "Failed to publish draft."
      );
    }

    yield put(publishSolutionSuccess());

    // Refresh both lists
    yield put(fetchDraftsRequest(projectId));
    yield put(fetchPublishedRequest(projectId));
  } catch (error) {
    console.error("Publish Solution Error:", error);

    yield put(publishSolutionFailure(error.message));
  }
}

// =========================
// UPDATE PUBLISHED
// =========================

function* updatePublishedSolutionSaga(action) {
  try {
    const token = TokenManager.getAccessToken();

    const { id, data, projectId } = action.payload;

    const normalizedPayload = normalizeSolutionFormPayload(data);

    const response = yield fetch(
      `${fastApiURL}/api/published/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(normalizedPayload),
      }
    );

    if (!response.ok) {
      const errorData = yield response.json();
      const errorMessage = errorData.detail 
        ? (typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail))
        : "Failed to update.";
      throw new Error(errorMessage);
    }

    yield put(updatePublishedSolutionSuccess());

    yield put(fetchPublishedRequest(projectId));
  } catch (error) {
    console.error("Update Published Solution Error:", error);
    yield put(updatePublishedSolutionFailure(error.message));
  }
}

// =========================
// DELETE PUBLISHED
// =========================

function* deletePublishedSolutionSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    
    const { id, projectId } = action.payload;

    const response = yield fetch(
      `${fastApiURL}/api/published/${id}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.detail || "Failed to delete published solution.");
    }

    yield put(deletePublishedSolutionSuccess());

    yield put(fetchPublishedRequest(projectId));
    
  } catch (error) {
    console.error("Delete Published Solution Error:", error);
    yield put(deletePublishedSolutionFailure(error.message));
  }
}

function* fetchPublishedSaga(action) {
  try {
    const token = TokenManager.getAccessToken();
    
    // 1. Extract the projectId from the action payload
    const projectId = action.payload; 

    if (!projectId) {
      throw new Error("projectId is required to fetch published solutions.");
    }

    // 2. Append project_id to the query string!
    const response = yield fetch(
      `${fastApiURL}/api/published/?project_id=${projectId}&page=1&page_size=100`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = yield response.json();
      throw new Error(errorData.detail || "Failed to fetch published solutions.");
    }

    const result = yield response.json();
    
    // Make sure you are passing the correct data array from the backend
    yield put(fetchPublishedSuccess(result.data || result)); 
  } catch (error) {
    console.error("Fetch Published Solutions Error:", error);
    yield put(fetchPublishedFailure(error.message));
  }
}

// =========================
// WATCHER
// =========================

export function* watchPublishedSolutionsSaga() {
  yield takeLatest(
    publishSolutionRequest.type,
    publishSolutionSaga
  );

  yield takeLatest(
    updatePublishedSolutionRequest.type,
    updatePublishedSolutionSaga
  );

  yield takeLatest(
    deletePublishedSolutionRequest.type,
    deletePublishedSolutionSaga
  );

  yield takeLatest(
    fetchPublishedRequest.type,
    fetchPublishedSaga
);
}